import { ref } from 'vue';
import { debounce } from '@chatwoot/utils';
import ImovelAPI from 'dashboard/api/imoveis';

export const createAddressSessionToken = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `addr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const predictionPlaceId = prediction =>
  prediction?.placeId || prediction?.place_id || '';

export const predictionFullText = prediction =>
  prediction?.fullText ||
  prediction?.full_text ||
  prediction?.description ||
  '';

export const predictionPrimaryText = prediction =>
  prediction?.primaryText ||
  prediction?.primary_text ||
  predictionFullText(prediction);

export const predictionSecondaryText = prediction =>
  prediction?.secondaryText || prediction?.secondary_text || '';

const normalizePredictions = data => {
  const predictions = data?.predictions || data?.data || [];
  return Array.isArray(predictions) ? predictions : [];
};

export const useAddressAutocomplete = ({
  minLength = 3,
  debounceMs = 300,
} = {}) => {
  const sessionToken = ref(null);
  const predictions = ref([]);
  const isAutocompleteLoading = ref(false);
  const autocompleteError = ref('');
  let searchSequence = 0;

  const ensureSessionToken = () => {
    if (!sessionToken.value) {
      sessionToken.value = createAddressSessionToken();
    }

    return sessionToken.value;
  };

  const resetSessionToken = () => {
    sessionToken.value = null;
  };

  const clearPredictions = () => {
    predictions.value = [];
    autocompleteError.value = '';
  };

  const loadPredictions = async rawQuery => {
    const query = String(rawQuery || '').trim();
    searchSequence += 1;
    const requestId = searchSequence;

    if (query.length < minLength) {
      clearPredictions();
      isAutocompleteLoading.value = false;
      return [];
    }

    const activeToken = ensureSessionToken();
    isAutocompleteLoading.value = true;
    autocompleteError.value = '';

    try {
      const { data } = await ImovelAPI.autocompleteAddress({
        query,
        session_token: activeToken,
      });

      if (requestId !== searchSequence) return predictions.value;

      predictions.value = normalizePredictions(data);
      sessionToken.value =
        data?.sessionToken || data?.session_token || activeToken;
      return predictions.value;
    } catch {
      if (requestId === searchSequence) {
        predictions.value = [];
        autocompleteError.value = 'Não foi possível buscar localizações agora.';
      }
      return [];
    } finally {
      if (requestId === searchSequence) {
        isAutocompleteLoading.value = false;
      }
    }
  };

  const debouncedLoadPredictions = debounce(loadPredictions, debounceMs, false);

  return {
    sessionToken,
    predictions,
    isAutocompleteLoading,
    autocompleteError,
    ensureSessionToken,
    resetSessionToken,
    clearPredictions,
    loadPredictions,
    debouncedLoadPredictions,
  };
};

export default useAddressAutocomplete;
